const CONSULTANT_ROLE_KEYS = ["consultant_admin", "consultant_advisor"];
const NETWORK_ADMIN_ROLE_KEYS = ["network_manager", "network_admin"];
const NETWORK_MEMBER_ROLE_KEYS = ["network_viewer", "network_user", "network_member"];
const NETWORK_ROLE_KEYS = [
  ...NETWORK_ADMIN_ROLE_KEYS,
  ...NETWORK_MEMBER_ROLE_KEYS,
];
const ORGANIZATION_ADMIN_ROLE_KEYS = ["organization_admin", "org_master", "org_admin"];
const ORGANIZATION_MEMBER_ROLE_KEYS = ["organization_user", "org_user"];
const ORGANIZATION_ROLE_KEYS = [
  ...ORGANIZATION_ADMIN_ROLE_KEYS,
  ...ORGANIZATION_MEMBER_ROLE_KEYS,
];

const normalizeRole = (value) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
};

const parseRoleEntry = (entry) => {
  if (typeof entry === "string") return normalizeRole(entry);
  if (!entry || typeof entry !== "object") return null;

  return (
    normalizeRole(entry.role) ||
    normalizeRole(entry.name) ||
    normalizeRole(entry.key) ||
    normalizeRole(entry.value)
  );
};

export const getUserRoles = (user) => {
  const membershipRoles = Array.isArray(user?.network_memberships)
    ? user.network_memberships
      .map((membership) => parseRoleEntry(membership?.role_name))
      .filter(Boolean)
    : [];

  if (Array.isArray(user?.roles) && user.roles.length > 0) {
    return Array.from(new Set([
      ...user.roles.map(parseRoleEntry).filter(Boolean),
      ...membershipRoles,
    ]));
  }

  const roles = new Set();

  const directRole = normalizeRole(user?.role);
  if (directRole) roles.add(directRole);

  const roleCollections = [user?.roles, user?.user_roles];
  roleCollections.forEach((collection) => {
    if (!Array.isArray(collection)) return;
    collection.forEach((entry) => {
      const parsed = parseRoleEntry(entry);
      if (parsed) roles.add(parsed);
    });
  });

  membershipRoles.forEach((role) => roles.add(role));

  if (user?.is_consultant_admin || user?.consultant_admin || user?.consultant_id) {
    roles.add("consultant_admin");
  }

  if (user?.is_consultant_advisor || user?.consultant_advisor || user?.advisor_id) {
    roles.add("consultant_advisor");
  }

  return Array.from(roles);
};

export const isConsultantUser = (user) => {
  const roles = getUserRoles(user);
  return roles.some((role) => CONSULTANT_ROLE_KEYS.includes(role));
};

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const normalizePermissions = (permissions) =>
  (Array.isArray(permissions) ? permissions : [])
    .map(normalizeRole)
    .filter(Boolean);

export const getNetworkMemberships = (user) => {
  const memberships = Array.isArray(user?.network_memberships) ? user.network_memberships : [];

  return memberships
    .map((membership) => {
      const networkId = toPositiveNumber(membership?.network_id ?? membership?.network?.id);
      if (!networkId) return null;

      return {
        ...membership,
        network_id: networkId,
        network_name: membership?.network_name ?? membership?.network?.name ?? `Network ${networkId}`,
        consultant_id: toPositiveNumber(membership?.consultant_id ?? membership?.consultant?.id),
        role_name: normalizeRole(membership?.role_name),
        context_role: normalizeRole(membership?.context_role),
        permissions: normalizePermissions(membership?.permissions),
      };
    })
    .filter(Boolean);
};

export const getNetworkMembership = (user, networkId) => {
  const normalizedNetworkId = toPositiveNumber(networkId);
  if (!normalizedNetworkId) return null;

  return getNetworkMemberships(user).find(
    (membership) => Number(membership.network_id) === normalizedNetworkId
  ) || null;
};

export const hasNetworkMembership = (user, networkId) =>
  Boolean(getNetworkMembership(user, networkId));

export const isNetworkUser = (user) => {
  const roles = getUserRoles(user);
  return getNetworkMemberships(user).length > 0 || roles.some((role) => NETWORK_ROLE_KEYS.includes(role));
};

export const isNetworkAdmin = (user, networkId) => {
  const membership = getNetworkMembership(user, networkId);
  if (!membership) return false;

  return (
    NETWORK_ADMIN_ROLE_KEYS.includes(membership.role_name) ||
    membership.context_role === "admin" ||
    membership.permissions.includes("manage_network")
  );
};

export const getPrimaryNetworkMembership = (user) =>
  getNetworkMemberships(user)[0] || null;

export const getNetworkRouteForUser = (user) => {
  const memberships = getNetworkMemberships(user);
  if (memberships.length === 1) return `/app/network/${memberships[0].network_id}`;
  if (memberships.length > 1) return "/app/network/select";
  return "/app/network";
};

export const isOrganizationUser = (user) => {
  if (user?.user_type === "tour") return false;

  const roles = getUserRoles(user);
  return user?.user_type === "org_user" || roles.some((role) => ORGANIZATION_ROLE_KEYS.includes(role));
};

export const isOrgScopedUser = (user) => isOrganizationUser(user);

export const isOrganizationAdmin = (user) => {
  const roles = getUserRoles(user);
  return roles.some((role) => ORGANIZATION_ADMIN_ROLE_KEYS.includes(role));
};

export const organizationRequiredRoles = [
  "tour",
  "org_user",
  ...ORGANIZATION_ROLE_KEYS,
];

export const getDefaultAuthenticatedRoute = (user) => {
  if (user?.user_type === "tour") return "/app/organization";
  if (isOrganizationUser(user)) return "/app/organization";
  if (isConsultantUser(user)) return "/app/consultant/dashboard";
  if (getUserRoles(user).includes("hke_admin")) return "/app/admin";
  if (isNetworkUser(user)) return getNetworkRouteForUser(user);
  return "/app/dashboard";
};

export const consultantRequiredRoles = [
  "tech",
  "admin",
  "user",
  "consultant_admin",
  "consultant_advisor",
];

export const networkRequiredRoles = [
  ...NETWORK_ROLE_KEYS,
];
